import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { SchrijvenScore, SchrijvenTaskType, SchrijvenVariantContext } from "@a2/core";
import { SCHRIJVEN_TASK_TYPES, SCHRIJVEN_TASK_LABELS } from "@a2/core";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import { Button } from "../../components/ui/Button";
import { SchrijvenRunnerShell } from "../../components/schrijven/SchrijvenRunnerShell";
import { SchrijvenGutter } from "../../components/schrijven/SchrijvenGutter";
import { SchrijvenWordMeter } from "../../components/schrijven/SchrijvenWordMeter";
import { SchrijvenTutorLive, useSchrijvenTips } from "../../components/schrijven/SchrijvenTutorLive";
import { SchrijvenFeedbackBubble } from "../../components/schrijven/SchrijvenFeedbackBubble";
import { EmailComposerShell } from "../../components/schrijven/EmailComposerShell";
import { WijkkrantShell } from "../../components/schrijven/WijkkrantShell";
import { FormShell } from "../../components/schrijven/FormShell";
import { BriefjeShell } from "../../components/schrijven/BriefjeShell";
import { ResumePromptDialog } from "../../components/schrijven/ResumePromptDialog";
import { ExitConfirmDialog } from "../../components/exam/ExitConfirmDialog";
import { useBulletCoverage } from "../../lib/schrijven/use-bullet-coverage";
import { useWordCount } from "../../lib/schrijven/use-word-count";
import { SCHRIJVEN_MIN_WORDS, registerToneFor } from "../../lib/schrijven/runner-constants";
import {
  clearResumableSession,
  loadResumableSession,
  saveResumableSession,
} from "../../lib/exam-session-resume";

type LocationState = {
  sessionId?: string;
  variantIds?: Partial<Record<SchrijvenTaskType, string>>;
};

type HydratedTurn = {
  id: string;
  taskType: SchrijvenTaskType;
  variantId: string | null;
  userText: string;
  scoreJson: string | null;
  variant: SchrijvenVariantContext | null;
  score: Record<string, number> | null;
};

type SessionData = {
  turns: HydratedTurn[];
};

type GradeResult = {
  score: SchrijvenScore;
  band: string;
  bandLabel: string;
  tutorParagraph: string;
  tutorParagraphTranslation: string | null;
};

type TurnState = {
  text: string;
  subject: string;
  submitting: boolean;
  result: GradeResult | null;
  error: string | null;
};

export function SchrijvenExamPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const state = (location.state ?? {}) as LocationState;

  const [sessionId, setSessionId] = useState<string | null>(state.sessionId ?? null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [activeTask, setActiveTask] = useState<SchrijvenTaskType>("email");
  const [turnStates, setTurnStates] = useState<Partial<Record<SchrijvenTaskType, TurnState>>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showResume, setShowResume] = useState(false);
  const [showExit, setShowExit] = useState(false);

  // Check for resumable session on mount.
  useEffect(() => {
    if (!sessionId) {
      const saved = loadResumableSession("exam");
      if (saved) setShowResume(true);
    }
  }, []);

  // Save resumable session when we have one.
  useEffect(() => {
    if (sessionId) saveResumableSession("exam", sessionId);
  }, [sessionId]);

  // Load session data from API.
  useEffect(() => {
    if (!sessionId) return;
    authedFetch(`/schrijven/sessions/${sessionId}`)
      .then((r) => r.json())
      .then((body: { ok: boolean; data?: SessionData; error?: { message: string } }) => {
        if (!body.ok) {
          setLoadError(body.error?.message ?? t("schrijven.runner.noSession"));
          return;
        }
        setSessionData(body.data ?? null);
      })
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : t("chrome.common.unknownError")));
  }, [sessionId]);

  function handleResume() {
    const saved = loadResumableSession("exam");
    if (saved) setSessionId(saved);
    setShowResume(false);
  }

  function handleDiscard() {
    clearResumableSession("exam");
    setShowResume(false);
  }

  async function submitTurn(taskType: SchrijvenTaskType) {
    if (!sessionId) return;
    const text = turnStates[taskType]?.text ?? "";
    if (!text.trim()) return;

    setTurnStates((prev) => ({
      ...prev,
      [taskType]: { ...turnFor(prev, taskType), text, submitting: true, error: null },
    }));

    try {
      const res = await authedFetch(`/schrijven/sessions/${sessionId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskType, userText: text }),
      });
      const body = (await res.json()) as
        | { ok: true; data: GradeResult }
        | { ok: false; error: { message: string } };

      if (!body.ok) {
        setTurnStates((prev) => ({
          ...prev,
          [taskType]: { ...turnFor(prev, taskType), submitting: false, error: body.error.message },
        }));
        return;
      }

      setTurnStates((prev) => ({
        ...prev,
        [taskType]: { ...turnFor(prev, taskType), submitting: false, result: body.data },
      }));
    } catch (e) {
      setTurnStates((prev) => ({
        ...prev,
        [taskType]: {
          ...turnFor(prev, taskType),
          submitting: false,
          error: e instanceof Error ? e.message : t("chrome.common.unknownError"),
        },
      }));
    }
  }

  function handleFinish() {
    clearResumableSession("exam");
    if (sessionId) navigate(`/schrijven/report/${sessionId}`);
  }

  const completedCount = SCHRIJVEN_TASK_TYPES.filter((tt) => !!turnStates[tt]?.result).length;
  const allDone = sessionData !== null && completedCount === SCHRIJVEN_TASK_TYPES.length;

  function handleClose() {
    if (completedCount > 0) setShowExit(true);
    else navigate("/schrijven");
  }

  if (!sessionId && !showResume) {
    return <EmptyState message={t("schrijven.runner.noSession")} back={t("schrijven.runner.backToSchrijven")} />;
  }
  if (loadError) {
    return <ErrorState message={loadError} back={t("schrijven.runner.backToSchrijven")} />;
  }
  if (!sessionData) {
    return (
      <>
        <ResumePromptDialog open={showResume} onResume={handleResume} onDiscard={handleDiscard} />
        <LoadingState message={t("schrijven.runner.loading")} />
      </>
    );
  }

  const activeTurn = sessionData.turns.find((tn) => tn.taskType === activeTask);
  const variant = activeTurn?.variant ?? null;
  const taskState = turnStates[activeTask] ?? emptyTurn();

  if (!variant) {
    return <ErrorState message={t("schrijven.runner.noVariant")} back={t("schrijven.runner.backToSchrijven")} />;
  }

  return (
    <>
      <ResumePromptDialog open={showResume} onResume={handleResume} onDiscard={handleDiscard} />
      <ExitConfirmDialog
        open={showExit}
        completedCount={completedCount}
        totalCount={SCHRIJVEN_TASK_TYPES.length}
        onCancel={() => setShowExit(false)}
        onConfirm={() => {
          setShowExit(false);
          handleFinish();
        }}
      />

      <RunnerBody
        key={activeTask}
        activeTask={activeTask}
        variant={variant}
        taskState={taskState}
        allDone={allDone}
        onClose={handleClose}
        onPickTask={setActiveTask}
        onPickNext={() => {
          const next = SCHRIJVEN_TASK_TYPES.find((tt) => tt !== activeTask && !turnStates[tt]?.result);
          if (next) setActiveTask(next);
        }}
        doneFor={(tt) => !!turnStates[tt]?.result}
        onText={(v) =>
          setTurnStates((prev) => ({
            ...prev,
            [activeTask]: { ...turnFor(prev, activeTask), text: v, result: null, error: null },
          }))
        }
        onSubject={(v) =>
          setTurnStates((prev) => ({
            ...prev,
            [activeTask]: { ...turnFor(prev, activeTask), subject: v },
          }))
        }
        onSubmit={() => submitTurn(activeTask)}
        onFinish={handleFinish}
      />
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Runner body — all per-turn hooks live here so the page's early returns stay
// above any hook calls. Remounted per active task via `key`.
// ───────────────────────────────────────────────────────────────────────────

function RunnerBody({
  activeTask,
  variant,
  taskState,
  allDone,
  onClose,
  onPickTask,
  onPickNext,
  doneFor,
  onText,
  onSubject,
  onSubmit,
  onFinish,
}: {
  activeTask: SchrijvenTaskType;
  variant: SchrijvenVariantContext;
  taskState: TurnState;
  allDone: boolean;
  onClose: () => void;
  onPickTask: (t: SchrijvenTaskType) => void;
  onPickNext: () => void;
  doneFor: (t: SchrijvenTaskType) => boolean;
  onText: (v: string) => void;
  onSubject: (v: string) => void;
  onSubmit: () => void;
  onFinish: () => void;
}) {
  const t = useT();
  const tips = useSchrijvenTips();
  const words = useWordCount(taskState.text);
  const covered = useBulletCoverage(variant.taskType, taskState.text, variant.bullets);

  const tone = registerToneFor(variant.recipientRelationship);
  const registerLabel =
    tone === "formal"
      ? t("schrijven.runner.registerFormal")
      : tone === "informal"
        ? t("schrijven.runner.registerInformal")
        : null;

  const graded = taskState.result;
  const taskLabel = SCHRIJVEN_TASK_LABELS[activeTask];

  const editorSlot = graded ? (
    <div className="flex-1 min-h-0 overflow-auto space-y-4">
      <SchrijvenFeedbackBubble
        score={graded.score}
        tutorParagraph={graded.tutorParagraph}
        tutorParagraphTranslation={graded.tutorParagraphTranslation}
        bullets={variant.bullets.map((b) => ({ text: b, covered: false }))}
      />
      {!allDone ? (
        <Button variant="ghost" size="md" onClick={onPickNext}>
          {t("schrijven.runner.nextTask")}
        </Button>
      ) : (
        <Button variant="primary" size="md" onClick={onFinish}>
          {t("schrijven.runner.toReport")}
        </Button>
      )}
    </div>
  ) : (
    <>
      <ComposerForType
        taskType={activeTask}
        variant={variant}
        value={taskState.text}
        onChange={onText}
        disabled={taskState.submitting}
        subject={taskState.subject}
        onSubjectChange={onSubject}
      />
      {taskState.error ? (
        <p role="alert" aria-live="assertive" className="text-sm text-[var(--fail)]">
          {taskState.error}
        </p>
      ) : null}
    </>
  );

  return (
    <SchrijvenRunnerShell
      taskLabel={taskLabel}
      stepCounter={{ n: SCHRIJVEN_TASK_TYPES.indexOf(activeTask) + 1, total: SCHRIJVEN_TASK_TYPES.length }}
      taskSwitcher={<TaskSwitcher active={activeTask} doneFor={doneFor} onPick={onPickTask} />}
      timer={null}
      onClose={onClose}
      opdracht={
        <SchrijvenGutter
          variant={variant}
          bulletCovered={graded ? undefined : covered}
          minWords={SCHRIJVEN_MIN_WORDS}
          registerLabel={registerLabel}
        />
      }
      opdrachtTitle={taskLabel}
      editor={editorSlot}
      tutor={graded ? null : <SchrijvenTutorLive tips={tips} />}
      wordMeter={<SchrijvenWordMeter words={words} min={SCHRIJVEN_MIN_WORDS} />}
      wordMeterCompact={<SchrijvenWordMeter words={words} min={SCHRIJVEN_MIN_WORDS} compact />}
      actions={
        graded ? null : (
          <>
            <Button variant="secondary" size="md" disabled>
              {t("schrijven.runner.saveDraft")}
            </Button>
            <Button
              variant="warm"
              size="md"
              onClick={onSubmit}
              disabled={taskState.submitting || !taskState.text.trim()}
            >
              {taskState.submitting ? t("schrijven.runner.submitBusy") : t("schrijven.runner.submit")}
              <SendIcon />
            </Button>
          </>
        )
      }
      actionsCompact={
        graded ? null : (
          <Button
            variant="warm"
            size="md"
            onClick={onSubmit}
            disabled={taskState.submitting || !taskState.text.trim()}
          >
            {taskState.submitting ? t("schrijven.runner.submitBusy") : t("schrijven.runner.submitShort")}
            <SendIcon />
          </Button>
        )
      }
    />
  );
}

function ComposerForType({
  taskType,
  variant,
  value,
  onChange,
  disabled,
  subject,
  onSubjectChange,
}: {
  taskType: SchrijvenTaskType;
  variant: SchrijvenVariantContext;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  subject: string;
  onSubjectChange: (v: string) => void;
}) {
  switch (taskType) {
    case "email":
      return (
        <EmailComposerShell
          variant={variant}
          value={value}
          onChange={onChange}
          disabled={disabled}
          subject={subject}
          onSubjectChange={onSubjectChange}
        />
      );
    case "wijkkrant":
      return <WijkkrantShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
    case "form":
      return <FormShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
    case "briefje":
      return <BriefjeShell variant={variant} value={value} onChange={onChange} disabled={disabled} />;
  }
}

function TaskSwitcher({
  active,
  doneFor,
  onPick,
}: {
  active: SchrijvenTaskType;
  doneFor: (t: SchrijvenTaskType) => boolean;
  onPick: (t: SchrijvenTaskType) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {SCHRIJVEN_TASK_TYPES.map((tt) => {
        const isActive = tt === active;
        return (
          <button
            key={tt}
            type="button"
            onClick={() => onPick(tt)}
            className={[
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono-badge text-[10px] uppercase tracking-wider transition-colors duration-150 ease-out",
              isActive
                ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--ink)]"
                : "border-[var(--border)] bg-transparent text-[var(--ink-muted)] hover:bg-[var(--panel-soft)]",
            ].join(" ")}
          >
            {doneFor(tt) ? <span style={{ color: "var(--pass)" }}>✓</span> : null}
            {SCHRIJVEN_TASK_LABELS[tt]}
          </button>
        );
      })}
    </div>
  );
}

function emptyTurn(): TurnState {
  return { text: "", subject: "", submitting: false, result: null, error: null };
}

function turnFor(
  states: Partial<Record<SchrijvenTaskType, TurnState>>,
  taskType: SchrijvenTaskType,
): TurnState {
  return states[taskType] ?? emptyTurn();
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function EmptyState({ message, back }: { message: string; back: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-dvh gap-6 px-6 bg-[var(--bg)]">
      <p className="text-[var(--ink-muted)]">{message}</p>
      <a
        href="/schrijven"
        className="inline-flex h-11 items-center rounded-[var(--radius)] bg-[var(--accent)] px-5 text-sm font-medium text-white hover:bg-[var(--accent-hover)] transition-colors"
      >
        {back}
      </a>
    </div>
  );
}

function ErrorState({ message, back }: { message: string; back: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-dvh gap-4 px-6 bg-[var(--bg)]">
      <p className="text-[var(--fail)] text-sm">{message}</p>
      <a href="/schrijven" className="text-sm text-[var(--ink-muted)] underline">
        {back}
      </a>
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-dvh bg-[var(--bg)]">
      <p className="text-[var(--ink-faint)] text-sm">{message}</p>
    </div>
  );
}
